import request from '@/util/request'
import {optionsToPaginationParams} from '@/util/pagination'
const DEFAULT_ITEMS_PER_PAGE = 50

let usersFetchRequestSequence = 0
let latestUsersFetchRequestId = 0

const state = {
  permissions: [],
  roleTemplates: {},
  usersWithPermissions: [],
  usersTotal: 0
}

const getters = {
  allPermissions: (state) => state.permissions,
  roleTemplates: (state) => state.roleTemplates,
  usersWithPermissions: (state) => state.usersWithPermissions,
  usersTotal: (state) => state.usersTotal
}

const mutations = {
  SET_PERMISSIONS_AND_TEMPLATES(state, {permissions, roleTemplates}) {
    state.permissions = permissions || []
    state.roleTemplates = roleTemplates || {}
  },
  SET_USERS_WITH_PERMISSIONS(state, {users = [], total = 0}) {
    state.usersWithPermissions = users
    state.usersTotal = total
  }
}

const actions = {
  async fetchPermissionsAndTemplates({commit}) {
    const permissionsData = await request.get('/admin-permissions')
    commit('SET_PERMISSIONS_AND_TEMPLATES', {
      permissions: permissionsData.permissions,
      roleTemplates: permissionsData.roleTemplates
    })
    return permissionsData
  },
  async fetchUsersWithPermissions({commit}, payload = {}) {
    const {search, tableOptions = {}} = payload
    const paginationParams = optionsToPaginationParams({
      page: tableOptions.page || 1,
      itemsPerPage: tableOptions.itemsPerPage || DEFAULT_ITEMS_PER_PAGE,
      sortBy: [],
      sortDesc: []
    })
    const trimmedSearch = (search || '').trim()
    const params = trimmedSearch
      ? {
        ...paginationParams,
        search: trimmedSearch
      }
      : paginationParams

    const requestId = ++usersFetchRequestSequence
    latestUsersFetchRequestId = requestId
    try {
      const usersData = await request.get('/admin-permissions/users', {params})
      if (requestId !== latestUsersFetchRequestId) {
        return usersData
      }
      commit('SET_USERS_WITH_PERMISSIONS', {
        users: usersData.users || [],
        total: usersData.pagination?.total ?? 0
      })
      return usersData
    } catch (error) {
      if (requestId === latestUsersFetchRequestId) {
        commit('SET_USERS_WITH_PERMISSIONS', {
          users: [],
          total: 0
        })
      }
      throw error
    }
  },
  async fetchData({dispatch}, payload = {}) {
    const {search, tableOptions} = payload
    await Promise.all([
      dispatch('fetchPermissionsAndTemplates'),
      dispatch('fetchUsersWithPermissions', {search, tableOptions})
    ])
  },
  async searchUsers(_context, {search}) {
    const searchResponse = await request.get('/admin-permissions/users/search', {
      params: {search}
    })
    return searchResponse.users || []
  },
  async updateUserPermissions(_context, {userId, permissionCodes}) {
    await request.put(`/admin-permissions/users/${userId}`, {
      permissionCodes
    })
  },
  async createRoleTemplate(_context, {name, description, permissions}) {
    await request.post('/admin-permissions/role-templates', {
      name,
      description,
      permissions,
    })
  },
  async updateRoleTemplate(_context, {templateKey, name, description, permissions}) {
    await request.put(`/admin-permissions/role-templates/${templateKey}`, {
      name,
      description,
      permissions,
    })
  },
  async deleteRoleTemplate(_context, {templateKey}) {
    await request.delete(`/admin-permissions/role-templates/${templateKey}`)
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
